import classNames from 'classnames';
import type { FC, LabelHTMLAttributes, ReactNode } from 'react';
import type { FormFieldBaseProps } from '../FormField/FormField.types';
import { FormFieldHelperText } from '../FormFieldHelperText/FormFieldHelperText';
import type { InteractiveAtomSize } from '../utils/InteractiveAtomProps';

export type LabeledControlSize = InteractiveAtomSize;

export type LabeledControlProps = Omit<
  FormFieldBaseProps,
  | 'error'
  | 'label'
  | 'labelTooltip'
  | 'primaryHelperText'
  | 'secondaryHelperText'
> & {
  /**
   * The control element (e.g., checkbox, radio button) to be rendered.
   *
   * To make labels work properly, the control must contain an element
   * triggered when wrapped inside a <label> tag by clicking on the label
   * (input type=checkbox|radio, button, etc.), or `htmlFor` should be
   * delivered via `labelProps`.
   */
  control: ReactNode;
  helperText?: ReactNode;
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
};

const sizeClassName: { [Key in LabeledControlSize]: string } = {
  small: 'text-xs',
  default: 'text-sm',
  large: 'text-base',
};

const gapClassName: { [Key in LabeledControlSize]: string } = {
  small: 'gap-3xs',
  default: 'gap-xs',
  large: 'gap-xs',
};

/**
 * A component that wraps Checkbox and Radio controls with a label element.
 *
 * Features:
 * - Provides associated label text for accessibility
 * - Displays optional description and helper text
 * - Enables toggling control state by clicking anywhere on the label
 * - Handles layout and spacing of elements
 *
 * Note: Switch components use a separate SwitchLabel component for their specific implementation.
 */
export const LabeledControl: FC<LabeledControlProps> = ({
  className,
  control,
  description,
  helperText,
  size = 'default',
  labelProps = undefined,
  disabled,
  ...restProps
}) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: All usages contain a nested input. Documented in the control prop.
  <label
    className={classNames(
      'flex',
      'mb-0', // this is a workaround to locally reset the default bottom margin of the label tag from bootstrap.min.css
      gapClassName[size],
      helperText ? 'items-start' : 'items-center',
      className,
    )}
    {...labelProps}
    {...restProps}
  >
    {control}
    <div className='flex flex-col gap-3xs'>
      {description && (
        <span
          className={classNames(
            'text-default font-normal',
            sizeClassName[size],
          )}
        >
          {description}
        </span>
      )}
      {helperText && (
        <FormFieldHelperText size={size} disabled={disabled} variant='plain'>
          {helperText}
        </FormFieldHelperText>
      )}
    </div>
  </label>
);
